import type React from 'react';

import { Badge, Link, Text } from '@change/ds-core-ui';
import { VictoryIcon } from '@change/ds-icons/VictoryIcon';

import { Translate, TranslatePlural } from '@change-corgi/core-react/i18n';

import { GuidesSection } from './components/GuidesSection';
import { ImpactImage } from './components/ImpactImage';
import { ImpactVideo } from './components/ImpactVideo';
import { SapContainer } from './components/SapContainer';
import { StarterQuoteContainer } from './components/StarterQuoteContainer';
import { usePageBody } from './hooks/usePageBody';

export const PageBody: React.FC<EmptyProps> = () => {
	const {
		data: { video, title, paragraphs, petitionUrl, badgeLabel, signatureCount, signatureCountLocalized, imageUrl },
		actions: { trackPetitionUrlClick },
	} = usePageBody();

	return (
		<>
			{video && <ImpactVideo view="mobile" video={video} title={title} />}
			{!video && imageUrl && <ImpactImage image={imageUrl} title={title} view="mobile" />}

			<div className="text-typography-strong container mt-6 max-w-[808px] md:mt-10" data-testid="impact-story-body">
				<div className="mb-16 space-y-6 md:space-y-12">
					<div className="flex flex-col items-start gap-2">
						<Badge
							leadingIcon={<VictoryIcon className="mr-2" />}
							size="medium"
							label={
								<TranslatePlural
									value="corgi.impact_story.badge"
									count={signatureCount}
									replacements={{ type: badgeLabel, signatureCount: signatureCountLocalized }}
								/>
							}
							variant="information"
						/>

						<Text as="h1" variant="heading" className="mb-0 md:text-4xl">
							{title}
						</Text>
					</div>

					{video && <ImpactVideo view="desktop" video={video} title={title} />}
					{!video && imageUrl && <ImpactImage image={imageUrl} title={title} view="desktop" />}

					<Text as="p" variant="body" size="large">
						{paragraphs[0]}
					</Text>

					<StarterQuoteContainer />

					{paragraphs.slice(1).map((paragraph, index) => (
						<Text key={`paragraph-${index}`} as="p" variant="body" size="large">
							{paragraph}
						</Text>
					))}

					<Link
						href={petitionUrl}
						data-testid="impact-story-page-petition-link"
						data-qa="impact-story-page-petition-link"
						onClick={trackPetitionUrlClick}
						className="mb-8 flex w-full items-center justify-center text-center"
						underline={false}
					>
						<Translate value="corgi.impact_story.link_to_petition" />
					</Link>

					<SapContainer />

					<GuidesSection />
				</div>
			</div>
		</>
	);
};
